import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { Button, Grid, Icon, Message } from "@intility/bifrost-react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useClient } from "../../../api/clients";
import { ClientObject } from "../../../components/ClientObject";
import { StandaloneClientHeader } from "../../../components/StandaloneClientHeader";
import { clientPath } from "../../../lib/clientPath";
import { themeClass } from "../../../lib/platformIdentity";

const neutralTheme = themeClass("neutral");

// The page of a standalone client: a header like a platform's (with a button
// row and no tabs) above the same client object as the platform's Clients tab.
// A client in a platform is viewed from its platform, so this page sends it
// there.
export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: client, isLoading, error } = useClient(id ?? "");

  if (isLoading) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid
          gap={16}
          style={{
            minHeight: "200px",
            placeContent: "center",
            textAlign: "center",
          }}
        >
          <Icon.Spinner size={32} />
          <p className="bf-p">Loading client details...</p>
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bf-content bfl-page-padding">
        <Grid gap={16}>
          <div>
            <Button
              variant="flat"
              onClick={() => navigate("/clients")}
              style={{ paddingLeft: 0 }}
            >
              <Icon icon={faArrowLeft} marginRight />
              Back to clients
            </Button>
          </div>
          <Message state="warning" header="Failed to load client">
            {error.message}
          </Message>
        </Grid>
      </div>
    );
  }

  if (!client) return null;

  // A link or an address that still leads to /clients/<id> for a client in a
  // platform: it is viewed from the platform.
  if (client.platformId) {
    // Keeping where it was opened from (My Hub), for the platform's breadcrumb.
    return <Navigate to={clientPath(client)} replace state={location.state} />;
  }

  return (
    // A standalone client has no platform to take a colour from: the page wears
    // the neutral (gray) theme, like a platform with no theme.
    <div className={`bf-content ${neutralTheme}`}>
      <StandaloneClientHeader client={client} />
      <div className="bf-page-padding">
        {/* The client object is 1200px wide at most; centred in the page. */}
        <div style={{ maxWidth: 1200, marginInline: "auto" }}>
          <ClientObject
            key={client.id}
            client={client}
            platformThemeClass={neutralTheme}
            onDeleted={() => navigate("/clients")}
          />
        </div>
      </div>
    </div>
  );
}
